import { useState, useMemo } from "react";
import { Input, SelectInput, Button, useDialog } from "@repo/ui";
import type { TaskManagerDTO } from "~/types/api-custom";
import { useUpdateTaskStatus } from "./use-update-task-status";

type Props = {
  task: TaskManagerDTO;
  onClose?: () => void;
};

export const ChangeStatusForm = ({ task, onClose }: Props) => {
  const { showDialog, closeDialog } = useDialog();
  const [isActive, setIsActive] = useState(task.active);
  const { handleUpdateStatus, isPending } = useUpdateTaskStatus();

  const statusOptions = [
    { label: "Active", value: "true" },
    { label: "Completed", value: "false" },
  ];

  const hasChanges = useMemo(() => {
    return isActive !== task.active;
  }, [isActive, task.active]);

  const handleSave = async () => {
    await handleUpdateStatus(task.id, isActive, onClose);
  };

  const handleCancel = () => {
    if (hasChanges) {
      showDialog?.({
        title: "Unsaved Changes",
        description: "You have unsaved changes. Are you sure you want to cancel without saving?",
        cancelText: "Keep Editing",
        confirmText: "Discard Changes",
        confirmVariant: "destructive",
        onConfirm: () => {
          closeDialog();
          onClose?.();
        },
      });
    } else {
      onClose?.();
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Input label="Task Name" required value={task.name} disabled></Input>

      {!task.active ? (
        <div className="flex flex-col gap-2">
          <Input label="Task Status" required value="Completed" disabled />
          <p className="text-xs text-gray-500">
            This task is completed and cannot be changed back.
          </p>
        </div>
      ) : (
        <SelectInput
          label="Task Status"
          options={statusOptions}
          placeholder="Select Status"
          value={isActive ? "true" : "false"}
          onChange={(val) => {
            const value = Array.isArray(val) ? val[0] : val;
            setIsActive(value === "true");
          }}
          required
        />
      )}

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={handleCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={handleSave} loading={isPending} disabled={!hasChanges || isPending}>
          Save
        </Button>
      </div>
    </div>
  );
};
